import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Switch, message, Tag, Space, Checkbox, Divider, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, LockOutlined, KeyOutlined, CopyOutlined } from '@ant-design/icons';
import api from '../../api/client';

const { Text } = Typography;

const UsersManager = () => {
    const [users, setUsers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);

    // User Edit Modal
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();

    // Password Reset Modal
    const [resetModalVisible, setResetModalVisible] = useState(false);
    const [tempPassword, setTempPassword] = useState('');
    const [currentResetUser, setCurrentResetUser] = useState(null);

    // Permissions Modal
    const [permModalVisible, setPermModalVisible] = useState(false);
    const [currentUserPerms, setCurrentUserPerms] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [selectedPermIds, setSelectedPermIds] = useState([]);
    const [savingPerms, setSavingPerms] = useState(false);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch users';
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const fetchBranches = async () => {
        try {
            const response = await api.get('/master/branches');
            setBranches(response.data);
        } catch (error) {
            console.error('Failed to fetch branches');
        }
    };

    const fetchAllPermissions = async () => {
        try {
            const response = await api.get('/admin/permissions');
            setAllPermissions(response.data);
        } catch (error) {
            console.error('Failed to fetch permissions');
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchBranches();
        fetchAllPermissions();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({ is_active: true, role: 'STAFF' });
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue({
            ...record,
            branch_id: record.branch_id,
            password: '', // Clear password field for editing
        });
        setIsModalVisible(true);
    };

    const handleResetPassword = async (record) => {
        try {
            const response = await api.post(`/admin/users/${record.id}/reset-password`);
            setTempPassword(response.data.temporary_password);
            setCurrentResetUser(record);
            setResetModalVisible(true);
        } catch (error) {
            message.error('Failed to reset password');
        }
    };

    const handleManagePermissions = async (record) => {
        setCurrentResetUser(record);
        setPermModalVisible(true);
        try {
            const response = await api.get(`/admin/users/${record.id}/permissions`);
            setSelectedPermIds(response.data.map(p => p.id));
        } catch (error) {
            message.error('Failed to fetch user permissions');
        }
    };

    const handleSavePermissions = async () => {
        setSavingPerms(true);
        try {
            await api.post(`/admin/users/${currentResetUser.id}/permissions`, {
                permissions: selectedPermIds
            });
            message.success('Permissions updated');
            setPermModalVisible(false);
        } catch (error) {
            message.error('Failed to save permissions');
        } finally {
            setSavingPerms(false);
        }
    };

    const handleDeactivate = async (id) => {
        try {
            await api.delete(`/admin/users/${id}`);
            message.success('User deactivated');
            fetchUsers();
        } catch (error) {
            message.error('Failed to deactivate user');
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingId) {
                if (!values.password) delete values.password;
                await api.put(`/admin/users/${editingId}`, values);
                message.success('User updated successfully');
            } else {
                await api.post('/admin/users', values);
                message.success('User created successfully');
            }
            setIsModalVisible(false);
            fetchUsers();
        } catch (error) {
            message.error('Operation failed: ' + (error.response?.data?.message || error.message));
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render: (role) => {
                const colors = { ADMIN: 'red', MANAGER: 'blue', STAFF: 'green', VIEWER: 'orange' };
                return <Tag color={colors[role] || 'default'}>{role}</Tag>;
            },
        },
        {
            title: 'Branch',
            dataIndex: ['branch', 'name'],
            key: 'branch',
            render: (text) => text || <span style={{ color: '#ccc' }}>All Branches</span>,
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button
                        icon={<LockOutlined />}
                        onClick={() => handleResetPassword(record)}
                        title="Reset Password"
                    />
                    <Button
                        icon={<KeyOutlined />}
                        onClick={() => handleManagePermissions(record)}
                        title="Manage Permissions"
                        disabled={record.role === 'ADMIN'}
                    />
                    {record.is_active && (
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                                Modal.confirm({
                                    title: 'Deactivate User',
                                    content: 'Are you sure you want to deactivate this user?',
                                    onOk: () => handleDeactivate(record.id)
                                });
                            }}
                        />
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>User Management</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                    Add User
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={users}
                rowKey="id"
                loading={loading}
            />

            {/* User Edit Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <UserOutlined style={{ fontSize: 20 }} />
                        <span>{editingId ? 'Edit User' : 'Add New User'}</span>
                    </div>
                }
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: 24 }}
                >
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 16
                    }}>
                        <Form.Item
                            name="name"
                            label="Full Name"
                            rules={[
                                { required: true, message: 'Please enter full name' },
                                { min: 3, message: 'Name must be at least 3 characters' }
                            ]}
                        >
                            <Input
                                placeholder="e.g., Juan Dela Cruz"
                                size="large"
                            />
                        </Form.Item>

                        <Form.Item
                            name="email"
                            label="Email Address"
                            rules={[
                                { required: true, message: 'Please enter email' },
                                { type: 'email', message: 'Please enter a valid email' }
                            ]}
                        >
                            <Input
                                placeholder="user@example.com"
                                size="large"
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="password"
                        label={editingId ? "New Password (Leave blank to keep current)" : "Password"}
                        rules={[
                            { required: !editingId, message: 'Please enter password' },
                            { min: 8, message: 'Password must be at least 8 characters' }
                        ]}
                        extra={editingId ? "Only fill this if you want to change the password" : "Minimum 8 characters"}
                    >
                        <Input.Password
                            placeholder={editingId ? "Enter new password" : "Enter password"}
                            size="large"
                        />
                    </Form.Item>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 16
                    }}>
                        <Form.Item
                            name="role"
                            label="User Role"
                            rules={[{ required: true, message: 'Please select role' }]}
                        >
                            <Select
                                placeholder="Select role"
                                size="large"
                            >
                                <Select.Option value="ADMIN">
                                    <Tag color="red">Admin</Tag> - Full Access
                                </Select.Option>
                                <Select.Option value="MANAGER">
                                    <Tag color="blue">Manager</Tag> - Management Access
                                </Select.Option>
                                <Select.Option value="STAFF">
                                    <Tag color="green">Staff</Tag> - Basic Access
                                </Select.Option>
                                <Select.Option value="VIEWER">
                                    <Tag color="orange">Viewer</Tag> - View Only
                                </Select.Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="branch_id"
                            label="Assigned Branch"
                        >
                            <Select
                                allowClear
                                placeholder="All branches"
                                size="large"
                            >
                                {branches.map(b => (
                                    <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="is_active"
                        valuePropName="checked"
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '12px 16px',
                            background: '#f5f5f5',
                            borderRadius: 8
                        }}>
                            <Switch />
                            <div>
                                <div style={{ fontWeight: 500 }}>Active Status</div>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    Inactive users cannot log in to the system
                                </div>
                            </div>
                        </div>
                    </Form.Item>

                    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setIsModalVisible(false)}>
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" size="large">
                                {editingId ? 'Update User' : 'Create User'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Password Reset Result Modal */}
            <Modal
                title="Password Reset Successful"
                open={resetModalVisible}
                onCancel={() => setResetModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setResetModalVisible(false)}>Close</Button>
                ]}
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Text type="secondary">Temporary password for <b>{currentResetUser?.email}</b>:</Text>
                    <div style={{
                        margin: '16px 0',
                        padding: '12px',
                        background: '#f8f9fa',
                        borderRadius: '4px',
                        border: '1px dashed #d9d9d9',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        <Text strong style={{ fontSize: '20px', fontFamily: 'monospace' }}>{tempPassword}</Text>
                        <Button
                            icon={<CopyOutlined />}
                            size="small"
                            onClick={() => {
                                navigator.clipboard.writeText(tempPassword);
                                message.success('Copied to clipboard');
                            }}
                        />
                    </div>
                    <Text type="danger" style={{ fontSize: '12px' }}>
                        Please provide this password to the user. They should change it after logging in.
                    </Text>
                </div>
            </Modal>

            {/* Permissions Management Modal */}
            <Modal
                title={
                    <Space>
                        <KeyOutlined />
                        <span>Manage Permissions: {currentResetUser?.name}</span>
                    </Space>
                }
                open={permModalVisible}
                onCancel={() => setPermModalVisible(false)}
                onOk={handleSavePermissions}
                confirmLoading={savingPerms}
                width={500}
                destroyOnClose
            >
                <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '10px 0' }}>
                    {allPermissions.length === 0 ? (
                        <Text type="secondary">No permissions defined. Go to "Permissions" tab to create them.</Text>
                    ) : (
                        <Checkbox.Group
                            style={{ width: '100%' }}
                            value={selectedPermIds}
                            onChange={setSelectedPermIds}
                        >
                            {allPermissions.map(p => (
                                <div key={p.id} style={{ marginBottom: '12px', padding: '8px', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
                                    <Checkbox value={p.id}>
                                        <Text strong>{p.name}</Text>
                                        <div style={{ marginLeft: '24px' }}>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>{p.description}</Text>
                                        </div>
                                    </Checkbox>
                                </div>
                            ))}
                        </Checkbox.Group>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default UsersManager;



